import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import type { SupportAssistantAction } from '../core/help/supportAssistant';
import { useAuth } from './AuthContext';

export type HelpChatPhase =
  | 'idle'
  | 'answering'
  | 'offering_handoff'
  | 'creating_ticket'
  | 'queued_live'
  | 'live'
  | 'error';

export interface HelpChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  actions?: SupportAssistantAction[];
  handoffOffer?: boolean;
}

export interface HelpChatSessionState {
  messages: HelpChatMessage[];
  draft: string;
  phase: HelpChatPhase;
  lastCategory: string;
  lastPriority: string;
  liveSessionId: string | null;
  setDraft: (v: string) => void;
  setPhase: (p: HelpChatPhase) => void;
  setLiveSessionId: (id: string | null) => void;
  setLastCategory: (v: string) => void;
  setLastPriority: (v: string) => void;
  appendMessages: (...msgs: HelpChatMessage[]) => void;
  resetForGreeting: (greeting: string) => void;
  clearSession: () => void;
  sendingLockRef: MutableRefObject<boolean>;
}

const HelpChatSessionContext = createContext<HelpChatSessionState | null>(null);

export function newHelpChatMessageId() {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function HelpChatSessionProvider({
  children,
  greeting,
}: {
  children: ReactNode;
  greeting: string;
}) {
  const { user } = useAuth();
  const userKey = user?.id ?? '';
  const sendingLockRef = useRef(false);
  const [messages, setMessages] = useState<HelpChatMessage[]>(() => [
    { id: newHelpChatMessageId(), role: 'assistant', text: greeting },
  ]);
  const [draft, setDraft] = useState('');
  const [phase, setPhase] = useState<HelpChatPhase>('idle');
  const [lastCategory, setLastCategory] = useState('other');
  const [lastPriority, setLastPriority] = useState('normal');
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const prevUserRef = useRef(userKey);
  const greetingRef = useRef(greeting);

  useEffect(() => {
    greetingRef.current = greeting;
  }, [greeting]);

  const clearSession = useCallback(() => {
    setMessages([{ id: newHelpChatMessageId(), role: 'assistant', text: greetingRef.current }]);
    setDraft('');
    setPhase('idle');
    setLiveSessionId(null);
    setLastCategory('other');
    setLastPriority('normal');
    sendingLockRef.current = false;
  }, []);

  useEffect(() => {
    if (prevUserRef.current === userKey) return;
    prevUserRef.current = userKey;
    clearSession();
  }, [userKey, clearSession]);

  const resetForGreeting = useCallback((text: string) => {
    setMessages([{ id: newHelpChatMessageId(), role: 'assistant', text }]);
  }, []);

  const appendMessages = useCallback((...msgs: HelpChatMessage[]) => {
    setMessages((h) => [...h, ...msgs]);
    if (msgs.some((m) => m.handoffOffer)) setPhase('offering_handoff');
  }, []);

  const value = useMemo<HelpChatSessionState>(() => ({
    messages,
    draft,
    phase,
    lastCategory,
    lastPriority,
    liveSessionId,
    setDraft,
    setPhase,
    setLiveSessionId,
    setLastCategory,
    setLastPriority,
    appendMessages,
    resetForGreeting,
    clearSession,
    sendingLockRef,
  }), [
    messages, draft, phase, lastCategory, lastPriority, liveSessionId,
    appendMessages, resetForGreeting, clearSession,
  ]);

  return (
    <HelpChatSessionContext.Provider value={value}>
      {children}
    </HelpChatSessionContext.Provider>
  );
}

export function useHelpChatSession() {
  const ctx = useContext(HelpChatSessionContext);
  if (!ctx) throw new Error('useHelpChatSession requires HelpChatSessionProvider');
  return ctx;
}
